"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@speakai/ui/button";
import { useToast } from "@speakai/ui/toast";
import { useQueryClient } from "@tanstack/react-query";
import { LoaderCircle } from "lucide-react";
import { useAddPaddleSource } from "@/features/subscription/mutations";
import { paymentKeys } from "@/queries/keys";
import { PaymentSourceType } from "@/features/subscription/types";
import type { PaymentMethod } from "@/features/subscription/types";
import { track, trackFailure } from "@/lib/integrations/sentry-tracking";

interface ConnectPaypalButtonProps {
  /** Receives the new Paddle source after reconciliation; `undefined` when the
   *  webhook hadn't landed in time and the source list is still empty. */
  onSuccess?: (newSource?: { id: string; type: "paddle" }) => void;
  onClose?: () => void;
  canManageBilling?: boolean;
  layout?: "full-width" | "centered" | "row";
}

// Bridges the lag between Paddle's `checkout.completed` event and the server
// webhook that actually persists the new payment method.
const PADDLE_WEBHOOK_RECONCILE_MS = 2000;

export function ConnectPaypalButton({
  onSuccess,
  onClose,
  canManageBilling = true,
  layout = "full-width",
}: ConnectPaypalButtonProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const addPaddleSource = useAddPaddleSource();
  const [isReconciling, setIsReconciling] = useState(false);

  // Refs back the state/callbacks so the window listener stays stable across
  // renders and never reads stale closures.
  const isReconcilingRef = useRef(false);
  const errorFiredRef = useRef(false);
  const timerRef = useRef<number | undefined>(undefined);
  const onSuccessRef = useRef(onSuccess);
  const onCloseRef = useRef(onClose);
  const overlayCardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onCloseRef.current = onClose;
  }, [onSuccess, onClose]);

  useEffect(() => {
    if (!isReconciling) return;
    const id = window.setTimeout(() => overlayCardRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [isReconciling]);

  useEffect(() => {
    function handlePaddleEvent(e: Event) {
      const detail = (e as CustomEvent).detail as
        | { name?: string; data?: { error?: { message?: string } } }
        | undefined;
      const name = detail?.name;
      if (name === "checkout.completed") {
        isReconcilingRef.current = true;
        errorFiredRef.current = false;
        setIsReconciling(true);
        timerRef.current = window.setTimeout(async () => {
          timerRef.current = undefined;
          await queryClient.invalidateQueries({ queryKey: paymentKeys.all });
          await queryClient.refetchQueries({
            queryKey: paymentKeys.sources(PaymentSourceType.Paddle),
          });
          if (errorFiredRef.current) {
            isReconcilingRef.current = false;
            setIsReconciling(false);
            return;
          }

          const list = queryClient.getQueryData<PaymentMethod[]>(
            paymentKeys.sources(PaymentSourceType.Paddle),
          );
          const newest = list && list.length > 0 ? list[list.length - 1] : undefined;

          // Clear the overlay before the toast so the toast paints above the
          // dialog rather than stacking at the same z-index.
          isReconcilingRef.current = false;
          setIsReconciling(false);

          if (newest) {
            toast.success(t("PAYMENT.PAYPAL_ADDED", "PayPal added successfully."));
            track("payment", "add_paypal");
            onSuccessRef.current?.({ id: newest.id, type: "paddle" });
          } else {
            toast.success(
              t(
                "PAYMENT.PAYPAL_ADDED_PENDING",
                "PayPal added, refresh if it doesn't appear shortly.",
              ),
            );
            trackFailure("payment", "paddle_reconcile_empty", {
              charge_type: "add_source",
              payment_method: "paddle",
            });
            onSuccessRef.current?.(undefined);
          }
          onCloseRef.current?.();
        }, PADDLE_WEBHOOK_RECONCILE_MS);
      } else if (name === "checkout.closed") {
        // Paddle fires `closed` immediately after `completed` on auto-dismiss;
        // we run the close ourselves at the end of reconciliation.
        if (!isReconcilingRef.current) onCloseRef.current?.();
      } else if (name === "checkout.error") {
        errorFiredRef.current = true;
        const message =
          detail?.data?.error?.message ??
          t("PAYMENT.CHECKOUT_FAILED", "Payment failed. Please try again.");
        toast.error(message.slice(0, 200));
      }
    }
    window.addEventListener("paddle:event", handlePaddleEvent);
    return () => {
      window.removeEventListener("paddle:event", handlePaddleEvent);
      if (timerRef.current !== undefined) {
        window.clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
      isReconcilingRef.current = false;
      errorFiredRef.current = false;
      setIsReconciling(false);
    };
  }, [t, toast, queryClient]);

  function handleClick() {
    addPaddleSource.mutate(undefined, {
      onError: () =>
        toast.error(t("PAYMENT.PAYPAL_INIT_FAILED", "Failed to initialize PayPal. Please try again.")),
    });
  }

  // PayPal brand yellow, required by PayPal brand guidelines, not a theme token.
  const PAYPAL_BRAND_CLASSES =
    "bg-[#FFC439] text-black hover:bg-[#FFB30C] focus-visible:ring-2 focus-visible:ring-ring";

  const reconcilingOverlay =
    isReconciling && typeof document !== "undefined"
      ? createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-background/80 backdrop-blur-sm"
            role="status"
            aria-live="assertive"
            aria-busy="true"
            data-testid="paypal-reconciling-overlay"
          >
            <div
              ref={overlayCardRef}
              tabIndex={-1}
              aria-modal="true"
              className="flex flex-col items-center gap-3 rounded-lg border border-border bg-card p-6 shadow-lg outline-none"
            >
              <LoaderCircle
                className="h-8 w-8 animate-spin motion-reduce:animate-none text-primary"
                aria-hidden="true"
              />
              <p className="text-sm font-medium">
                {t("PAYMENT.PAYPAL_FINALIZING", "Finalizing PayPal…")}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("PAYMENT.PAYPAL_FINALIZING_DESC", "Saving your payment method")}
              </p>
            </div>
          </div>,
          document.body,
        )
      : null;

  if (layout === "row") {
    const isDisabled = !canManageBilling || addPaddleSource.isPending || isReconciling;
    return (
      <>
        <button
          type="button"
          onClick={handleClick}
          disabled={isDisabled}
          data-testid="select-source-connect-paypal"
          aria-label={t("PAYMENT.CONNECT_PAYPAL", "Connect PayPal")}
          className="flex w-full items-center justify-between rounded-md border border-border p-3 text-left transition-colors hover:border-muted-foreground/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="flex items-center gap-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted/30" aria-hidden="true">
              <img
                src="/assets/media/icons/pricing/paypal-icon.svg"
                alt=""
                aria-hidden="true"
                className="h-5 w-auto"
              />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-medium">{t("PAYMENT.PAYPAL", "PayPal")}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {t("PAYMENT.CONNECT_PAYPAL_HINT", "Pay with your PayPal account")}
              </span>
            </span>
          </span>
          <span className="ml-3 inline-flex items-center text-xs font-medium text-primary">
            {addPaddleSource.isPending ? (
              <LoaderCircle aria-hidden="true" className="h-4 w-4 animate-spin motion-reduce:animate-none" />
            ) : (
              t("ACTION.CONNECT", "Connect")
            )}
          </span>
        </button>
        {reconcilingOverlay}
      </>
    );
  }

  const wrapperClass =
    layout === "centered" ? "flex flex-col items-center gap-3" : "w-full";
  const buttonClass =
    layout === "centered" ? PAYPAL_BRAND_CLASSES : `w-full ${PAYPAL_BRAND_CLASSES}`;
  const resolvedTestId =
    layout === "centered" ? "add-source-paddle-button" : "select-source-connect-paypal";

  return (
    <>
      <div className={wrapperClass}>
        <Button
          type="button"
          variant="primary"
          size="lg"
          className={buttonClass}
          disabled={!canManageBilling || addPaddleSource.isPending || isReconciling}
          onClick={handleClick}
          data-testid={resolvedTestId}
          aria-label={t("PAYMENT.CONNECT_PAYPAL", "Connect PayPal")}
        >
          <span className="mr-2">{t("ACTION.CONNECT", "Connect")}</span>
          <img
            src="/assets/media/icons/pricing/paypal.png"
            alt=""
            aria-hidden="true"
            className="h-5 w-auto"
          />
          {addPaddleSource.isPending && (
            <LoaderCircle aria-hidden="true" className="ml-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
          )}
        </Button>
      </div>
      {reconcilingOverlay}
    </>
  );
}
